import type { AuditScheduleFrequency } from './project';
import type { AsoEngineType } from './aso';

// ======================================================================================|
// MARK: Currency

export type SupportedCurrency = 'eur' | 'usd' | 'gbp';
export const SUPPORTED_CURRENCIES: SupportedCurrency[] = ['eur', 'usd', 'gbp'];
export const DEFAULT_CURRENCY: SupportedCurrency = 'usd';

/** Default trial length for self-serve no-card signups. */
export const SELF_SERVE_TRIAL_DAYS = 7;

/** The canonical pricing currency. All other currencies are derived from EUR via FX rates. */
export const CANONICAL_CURRENCY: SupportedCurrency = 'eur';

/** Drift threshold (fraction) before FX repricing is triggered. */
export const FX_DRIFT_THRESHOLD = 0.05;

export interface CurrencyConfig {
  code: SupportedCurrency;
  symbol: string;
  name: string;
  locale: string;
}

export const CURRENCY_CONFIG: Record<SupportedCurrency, CurrencyConfig> = {
  eur: { code: 'eur', symbol: '€', name: 'Euro', locale: 'de-DE' },
  usd: { code: 'usd', symbol: '$', name: 'US Dollar', locale: 'en-US' },
  gbp: { code: 'gbp', symbol: '£', name: 'British Pound', locale: 'en-GB' },
};

/**
 * Map ISO 3166-1 alpha-2 country code to a supported currency.
 * Eurozone countries → EUR, US → USD, GB → GBP, all others → USD (default).
 */
const EUROZONE_COUNTRIES = new Set([
  'AT',
  'BE',
  'CY',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PT',
  'SK',
  'SI',
  'ES',
  'HR',
]);

export function countryToCurrency(countryCode: string): SupportedCurrency {
  const upper = countryCode.toUpperCase();
  if (upper === 'US') return 'usd';
  if (upper === 'GB') return 'gbp';
  if (EUROZONE_COUNTRIES.has(upper)) return 'eur';
  return DEFAULT_CURRENCY;
}

// ======================================================================================|
// MARK: Plan Tiers & Identifiers

export type PlanTier = 'free' | 'basic' | 'pro' | 'elite' | 'enterprise';
export type PlanId = 'free' | 'basic' | 'pro' | 'elite' | 'enterprise';
export type BillingInterval = 'monthly' | 'annual';
export type BillingSource = 'free' | 'stripe' | 'admin_override' | 'sandbox';
export const LEGACY_PLAN_TIER_ALIASES = {
  starter: 'basic',
  growth: 'pro',
  scale: 'elite',
} as const;
export type LegacyPlanTier = keyof typeof LEGACY_PLAN_TIER_ALIASES;

/**
 * Billing status for an organization
 * - trialing: In trial period
 * - active: Paid and current
 * - past_due: Payment failed, retrying
 * - grace_overage: Over quota, grace period active
 * - canceled: Subscription canceled
 * - sandbox: Development/testing mode
 */
export const ORG_BILLING_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'grace_overage',
  'canceled',
  'trial_expired',
  'sandbox',
] as const;

export type OrgBillingStatus = (typeof ORG_BILLING_STATUSES)[number];

// ======================================================================================|
// MARK: Plan Features & Pricing

export interface PlanFeatures {
  // Capacity
  maxProjects: number;
  maxTeamSeats: number;
  maxMarketsPerProject: number;
  /**
   * A7Y-5533 — swaps a project may make in its fixed 20-prompt AI Discovery
   * set (a swap or a thrown-out prompt each count one; a restore refunds
   * none). One cap per project, whatever apps and markets it tracks. 0 makes
   * the set read-only. Concrete per-tier cap (NOT `-1`/unlimited).
   */
  maxCustomDiscoveryPromptsPerProject: number;

  // Audit cadence
  maxAuditFrequency: 'manual_only' | 'monthly' | 'biweekly' | 'weekly' | '2x_week' | 'daily';

  // Competitors
  maxCompetitorsPerProject: number;
  maxTotalCompetitors: number | null;
  /**
   * A7Y-4702 — tracked-keyword markets (store × country × language targets)
   * per storefront, primary included; mirrors maxCompetitorsPerProject.
   */
  maxRankTrackingMarketsPerStore: number;

  // Data retention
  dataRetentionDays: number;

  // Core feature flags
  includesStoreAnalytics: boolean;
  includesPublishing: boolean;
  assetGenerationLevel: 'none' | 'text' | 'text_and_images';
  includesLocalization: boolean;
  includesApiAccess: boolean;
  includesSlackIntegration: boolean;
  includesFigmaPlugin: boolean;
  includesPrioritySupport: boolean;

  // Audit modules
  includesReviewSentiment: boolean;
  reviewsLookbackDays: number | null;
  includesKeywordAnalysis: boolean;
  aiDiscoverabilityLLMs: string[];
  includesCompetitorDiscovery: boolean;
  includesIntentMapping: boolean;
  includesPolicyChecks: boolean;

  // In-app events (A7Y-3845)
  includesInAppEvents: boolean;
  /**
   * Org-wide standing count of `AppEventObservations` records the org can
   * see (competitor event observations), enforced as a read cap that
   * withholds the oldest records past the limit. `-1` on enterprise by
   * decision A7Y-2967/A7Y-3845, unlike `maxCustomDiscoveryPromptsPerProject`.
   */
  maxTrackedCompetitorEvents: number;
  /**
   * Org-wide standing count of live event-nomination receipts (not past or
   * archived). `-1` on enterprise by decision A7Y-2967/A7Y-3845, unlike
   * `maxCustomDiscoveryPromptsPerProject`.
   */
  maxEventNominations: number;
  /**
   * Org-wide standing count of platform-created (`createdVia: "apptonomy"`)
   * ASC event drafts, per distinct ASC app. `-1` on enterprise by decision
   * A7Y-2967/A7Y-3845, unlike `maxCustomDiscoveryPromptsPerProject`.
   */
  maxAscEventDrafts: number;
}

export interface CurrencyPricing {
  monthly: number; // in minor units: cents (EUR/USD) or pence (GBP)
  annual: number;
  annualDiscount?: number; // percentage (e.g. 17)
}

export interface PlanPricing {
  currencies: Record<SupportedCurrency, CurrencyPricing>;
}

export type StripeEnvironment = 'sandbox' | 'production';

export interface StripePlanIds {
  productId: string;
  /** Single price ID per interval — Stripe currency_options handles multi-currency. */
  priceIds: { monthly: string; annual: string };
}

export interface PlanDefinition {
  id: string;
  tier: PlanTier;
  name: string;
  description: string;
  pricing: PlanPricing;
  features: PlanFeatures;
  stripe: Record<StripeEnvironment, StripePlanIds>;
  isActive: boolean;
  displayOrder: number;
  metadata?: Record<string, unknown>;
}

// ======================================================================================|
// MARK: PLAN_TIERS Constant

export const PLAN_TIERS: Record<PlanTier, PlanDefinition> = {
  free: {
    id: 'free',
    tier: 'free',
    name: 'Free',
    description: 'Get started with monthly ASO audits — no credit card required',
    pricing: {
      currencies: {
        eur: { monthly: 0, annual: 0 },
        usd: { monthly: 0, annual: 0 },
        gbp: { monthly: 0, annual: 0 },
      },
    },
    features: {
      maxProjects: 1,
      maxTeamSeats: 1,
      maxMarketsPerProject: 1,
      maxCustomDiscoveryPromptsPerProject: 0,
      maxAuditFrequency: 'monthly',
      maxCompetitorsPerProject: 1,
      maxRankTrackingMarketsPerStore: 1,
      maxTotalCompetitors: 1,
      dataRetentionDays: 60,
      includesStoreAnalytics: false,
      includesPublishing: false,
      assetGenerationLevel: 'none',
      includesLocalization: false,
      includesApiAccess: false,
      includesSlackIntegration: false,
      includesFigmaPlugin: false,
      includesPrioritySupport: false,
      includesReviewSentiment: false,
      reviewsLookbackDays: null,
      includesKeywordAnalysis: true,
      aiDiscoverabilityLLMs: ['chatgpt'],
      includesCompetitorDiscovery: true,
      includesIntentMapping: false,
      includesPolicyChecks: false,
      includesInAppEvents: false,
      maxTrackedCompetitorEvents: 0,
      maxEventNominations: 0,
      maxAscEventDrafts: 0,
    },
    stripe: {
      sandbox: { productId: '', priceIds: { monthly: '', annual: '' } },
      production: { productId: '', priceIds: { monthly: '', annual: '' } },
    },
    isActive: true,
    displayOrder: 0,
  },
  basic: {
    id: 'basic',
    tier: 'basic',
    name: 'Basic',
    description: 'For indie devs getting serious about ASO',
    pricing: {
      currencies: {
        eur: { monthly: 4900, annual: annualFromMonthly(4900), annualDiscount: 17 },
        usd: { monthly: 4900, annual: annualFromMonthly(4900), annualDiscount: 17 },
        gbp: { monthly: 3900, annual: annualFromMonthly(3900), annualDiscount: 17 },
      },
    },
    features: {
      maxProjects: 1,
      maxTeamSeats: 1,
      maxMarketsPerProject: 2,
      maxCustomDiscoveryPromptsPerProject: 0,
      maxAuditFrequency: 'weekly',
      maxCompetitorsPerProject: 3,
      maxRankTrackingMarketsPerStore: 3,
      maxTotalCompetitors: 3,
      dataRetentionDays: 90,
      includesStoreAnalytics: false,
      includesPublishing: false,
      assetGenerationLevel: 'none',
      includesLocalization: true,
      includesApiAccess: false,
      includesSlackIntegration: false,
      includesFigmaPlugin: true,
      includesPrioritySupport: false,
      includesReviewSentiment: true,
      reviewsLookbackDays: 30,
      includesKeywordAnalysis: true,
      aiDiscoverabilityLLMs: ['chatgpt', 'claude'],
      includesCompetitorDiscovery: true,
      includesIntentMapping: false,
      includesPolicyChecks: false,
      includesInAppEvents: false,
      maxTrackedCompetitorEvents: 0,
      maxEventNominations: 0,
      maxAscEventDrafts: 0,
    },
    stripe: {
      sandbox: { productId: '', priceIds: { monthly: '', annual: '' } },
      production: { productId: '', priceIds: { monthly: '', annual: '' } },
    },
    isActive: true,
    displayOrder: 1,
  },
  pro: {
    id: 'pro',
    tier: 'pro',
    name: 'Pro',
    description: 'For small teams publishing across multiple stores',
    pricing: {
      currencies: {
        eur: { monthly: 14900, annual: annualFromMonthly(14900), annualDiscount: 17 },
        usd: { monthly: 14900, annual: annualFromMonthly(14900), annualDiscount: 17 },
        gbp: { monthly: 12900, annual: annualFromMonthly(12900), annualDiscount: 17 },
      },
    },
    features: {
      maxProjects: 5,
      maxTeamSeats: 2,
      maxMarketsPerProject: 5,
      maxCustomDiscoveryPromptsPerProject: 5,
      maxAuditFrequency: '2x_week',
      maxCompetitorsPerProject: 5,
      maxRankTrackingMarketsPerStore: 5,
      maxTotalCompetitors: null,
      dataRetentionDays: 365,
      includesStoreAnalytics: true,
      includesPublishing: true,
      assetGenerationLevel: 'text',
      includesLocalization: true,
      includesApiAccess: false,
      includesSlackIntegration: false,
      includesFigmaPlugin: true,
      includesPrioritySupport: true,
      includesReviewSentiment: true,
      reviewsLookbackDays: 60,
      includesKeywordAnalysis: true,
      aiDiscoverabilityLLMs: ['chatgpt', 'claude', 'gemini'],
      includesCompetitorDiscovery: true,
      includesIntentMapping: true,
      includesPolicyChecks: true,
      includesInAppEvents: true,
      maxTrackedCompetitorEvents: 300,
      maxEventNominations: 10,
      maxAscEventDrafts: 10,
    },
    stripe: {
      sandbox: { productId: '', priceIds: { monthly: '', annual: '' } },
      production: { productId: '', priceIds: { monthly: '', annual: '' } },
    },
    isActive: true,
    displayOrder: 2,
  },
  elite: {
    id: 'elite',
    tier: 'elite',
    name: 'Elite',
    description: 'For agencies with large, multilingual portfolios',
    pricing: {
      currencies: {
        eur: { monthly: 39900, annual: annualFromMonthly(39900), annualDiscount: 17 },
        usd: { monthly: 39900, annual: annualFromMonthly(39900), annualDiscount: 17 },
        gbp: { monthly: 34900, annual: annualFromMonthly(34900), annualDiscount: 17 },
      },
    },
    features: {
      maxProjects: 20,
      maxTeamSeats: 5,
      maxMarketsPerProject: 10,
      maxCustomDiscoveryPromptsPerProject: 10,
      maxAuditFrequency: 'daily',
      maxCompetitorsPerProject: 10,
      maxRankTrackingMarketsPerStore: 10,
      maxTotalCompetitors: null,
      dataRetentionDays: 730,
      includesStoreAnalytics: true,
      includesPublishing: true,
      assetGenerationLevel: 'text_and_images',
      includesLocalization: true,
      includesApiAccess: false,
      includesSlackIntegration: true,
      includesFigmaPlugin: true,
      includesPrioritySupport: true,
      includesReviewSentiment: true,
      reviewsLookbackDays: 365,
      includesKeywordAnalysis: true,
      aiDiscoverabilityLLMs: ['chatgpt', 'claude', 'gemini', 'perplexity', 'google_ai_overview'],
      includesCompetitorDiscovery: true,
      includesIntentMapping: true,
      includesPolicyChecks: true,
      includesInAppEvents: true,
      maxTrackedCompetitorEvents: 1200,
      maxEventNominations: 40,
      maxAscEventDrafts: 40,
    },
    stripe: {
      sandbox: { productId: '', priceIds: { monthly: '', annual: '' } },
      production: { productId: '', priceIds: { monthly: '', annual: '' } },
    },
    isActive: true,
    displayOrder: 3,
  },
  enterprise: {
    id: 'enterprise',
    tier: 'enterprise',
    name: 'Enterprise',
    description: 'Flexible, custom solutions — contact sales',
    pricing: {
      currencies: {
        eur: { monthly: 0, annual: 0 },
        usd: { monthly: 0, annual: 0 },
        gbp: { monthly: 0, annual: 0 },
      },
    },
    features: {
      maxProjects: -1,
      maxTeamSeats: -1,
      maxMarketsPerProject: 30,
      // A7Y-1707 — concrete 10 (NOT -1): each active custom prompt amplifies
      // LLM fan-out cost, so even enterprise is capped.
      maxCustomDiscoveryPromptsPerProject: 20,
      maxAuditFrequency: 'daily',
      maxCompetitorsPerProject: 20,
      maxRankTrackingMarketsPerStore: 20,
      maxTotalCompetitors: null,
      dataRetentionDays: -1,
      includesStoreAnalytics: true,
      includesPublishing: true,
      assetGenerationLevel: 'text_and_images',
      includesLocalization: true,
      includesApiAccess: true,
      includesSlackIntegration: true,
      includesFigmaPlugin: true,
      includesPrioritySupport: true,
      includesReviewSentiment: true,
      reviewsLookbackDays: -1,
      includesKeywordAnalysis: true,
      aiDiscoverabilityLLMs: ['chatgpt', 'claude', 'gemini', 'perplexity', 'google_ai_overview'],
      includesCompetitorDiscovery: true,
      includesIntentMapping: true,
      includesPolicyChecks: true,
      includesInAppEvents: true,
      maxTrackedCompetitorEvents: -1,
      maxEventNominations: -1,
      maxAscEventDrafts: -1,
    },
    stripe: {
      sandbox: { productId: '', priceIds: { monthly: '', annual: '' } },
      production: { productId: '', priceIds: { monthly: '', annual: '' } },
    },
    isActive: true,
    displayOrder: 4,
  },
};

export function resolvePlanTier(planTier: string | null | undefined): PlanTier | null {
  if (!planTier) return null;

  if (Object.prototype.hasOwnProperty.call(PLAN_TIERS, planTier)) {
    return planTier as PlanTier;
  }

  if (Object.prototype.hasOwnProperty.call(LEGACY_PLAN_TIER_ALIASES, planTier)) {
    return LEGACY_PLAN_TIER_ALIASES[planTier as LegacyPlanTier];
  }

  return null;
}

export function normalizePlanTier(
  planTier: string | null | undefined,
  fallback: PlanTier = 'free'
): PlanTier {
  return resolvePlanTier(planTier) ?? fallback;
}

/**
 * Ordinal rank for plan tiers, from lowest → highest. Used by `maxPlanTier`
 * to pick the higher of two tiers. Single source of truth — do NOT duplicate
 * this map elsewhere (A7Y-1833).
 */
export const PLAN_TIER_RANK: Record<PlanTier, number> = {
  free: 0,
  basic: 1,
  pro: 2,
  elite: 3,
  enterprise: 4,
};

/**
 * Grant-only effective tier: the higher of a base (billed) tier and an optional
 * override tier. An admin plan override can only ever *raise* the effective
 * feature tier, never lower it below what the org already pays for.
 *
 * A7Y-1833 — decouples feature entitlement from billing display. The billed
 * tier still drives price/name; this helper drives feature gating.
 */
export function maxPlanTier(
  base: string | null | undefined,
  override: string | null | undefined
): PlanTier {
  const b = normalizePlanTier(base);
  const o = resolvePlanTier(override); // null when no (valid) override
  if (!o) return b;
  return PLAN_TIER_RANK[o] > PLAN_TIER_RANK[b] ? o : b;
}

// ======================================================================================|
// MARK: Add-Ons

export interface StripeAddOnIds {
  productId: string;
  /** Single price ID — Stripe currency_options handles multi-currency. */
  priceId: string;
}

export interface AddOnDefinition {
  id: string;
  name: string;
  description: string;
  pricing: Record<SupportedCurrency, { monthly: number }>;
  stripe: Record<StripeEnvironment, StripeAddOnIds>;
  compatiblePlans: PlanTier[];
  stackable: boolean;
}

export const ADD_ONS: Record<string, AddOnDefinition> = {
  extra_project: {
    id: 'extra_project',
    name: 'Extra Project Slot',
    description: '+1 additional project, stackable',
    pricing: {
      eur: { monthly: 5000 },
      usd: { monthly: 5000 },
      gbp: { monthly: 4000 },
    },
    stripe: {
      sandbox: {
        productId: 'prod_U056MNMZTZYOmX',
        priceId: '',
      },
      production: {
        productId: 'prod_U057159T76i820',
        priceId: '',
      },
    },
    compatiblePlans: ['basic', 'pro', 'elite', 'enterprise'],
    stackable: true,
  },
};

// ======================================================================================|
// MARK: Affiliate Referral Pointer
//
// A7Y-1895: embedded pointer on OrgLedger recording which affiliate referral
// acquired this org. Renamed from `RewardfulReferral` to avoid colliding with
// the new `AffiliateReferral` collection doc in affiliate.ts — this remains a
// distinct, narrow pointer shape (id + coupon snapshot), not the full referral.

export interface AffiliateReferralRef {
  referralId: string;
  affiliateId: string;
  couponCode: string | null;
  acquiredAt: string;
}

// ======================================================================================|
// MARK: Transaction Payloads

export interface CheckoutPayload {
  planId: PlanId;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  maxActiveProjects: number;
  billingCadence: 'monthly' | 'annual';
  currency: SupportedCurrency;
}

export interface SubscriptionUpdatePayload {
  previousPlanId: PlanId;
  newPlanId: PlanId;
  previousMaxProjects: number;
  newMaxProjects: number;
  effectiveDate: string;
}

export interface ProjectActivationPayload {
  projectId: string;
  projectName: string;
  previousCount: number;
  newCount: number;
}

export interface ManualAdjustmentPayload {
  adjustedBy: string;
  adjustmentType: 'quota_override' | 'status_change' | 'plan_override';
  previousValue: unknown;
  newValue: unknown;
  justification: string;
}

export interface TrialPayload {
  trialDays: number;
  trialEndsAt: string;
}

export interface GracePayload {
  graceDays: number;
  graceEndsAt: string;
  overageCount: number;
}

export interface DeltaAddOns {
  add: string[];
  remove: string[];
}

export type TransactionPayload =
  | CheckoutPayload
  | SubscriptionUpdatePayload
  | ProjectActivationPayload
  | ManualAdjustmentPayload
  | TrialPayload
  | GracePayload
  | Record<string, unknown>;

// ======================================================================================|
// MARK: Ledger Transaction

export type LedgerTransactionType =
  | 'checkout_completed'
  | 'subscription_updated'
  | 'subscription_canceled'
  | 'project_activated'
  | 'project_archived'
  | 'manual_adjustment'
  | 'webhook_sync'
  | 'trial_started'
  | 'trial_expired'
  | 'grace_started'
  | 'grace_expired'
  | 'payment_succeeded'
  | 'payment_failed'
  | 'refund_issued'
  | 'tier_migration_2026'
  | 'downgrade_scheduled'
  | 'downgrade_schedule_canceled';

export type LedgerTransactionStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export interface LedgerTransaction {
  transactionId: string;
  orgId: string;
  type: LedgerTransactionType;
  timestamp: string;
  deltaProjects: number;
  deltaAddOns: DeltaAddOns | null;
  stripeEventId: string | null;
  payload: TransactionPayload;
  appliedBy: string;
  reason: string | null;
}

// ======================================================================================|
// MARK: Usage & Quotas

export interface UsageMetrics {
  projects: number;
  markets: number;
  teamMembers: number;
}

export interface BillingPeriod {
  start: Date | string;
  end: Date | string;
  usage: UsageMetrics;
}

// ======================================================================================|
// MARK: Lifecycle Emails

export type LifecycleEmailType =
  | 'day1_no_audit'
  | 'day3_no_audit'
  | 'day3_has_audit'
  | 'day6_no_audit'
  | 'day6_has_audit';

export interface LifecycleEmailRecord {
  type: LifecycleEmailType;
  sentAt: string;
  resendId: string;
  variant: 'no_audit' | 'has_audit';
}

// ======================================================================================|
// MARK: Org Ledger

/** A plan change that Stripe applies at the end of the current billing period (A7Y-5184). */
export interface PendingPlanChange {
  planId: PlanId;
  billingCadence: 'monthly' | 'annual';
  /** ISO; Stripe enters the schedule's second phase here (= period end when scheduled). */
  effectiveAt: string;
  stripeScheduleId: string;
  scheduledAt: string;
}

export interface OrgLedger {
  id: string;
  orgId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string | null;
  planId: PlanId;
  billingCadence: 'monthly' | 'annual';
  currency: SupportedCurrency;
  status: OrgBillingStatus;
  renewalDate: string | null;
  maxActiveProjects: number;
  activeProjectCount: number;
  affiliateReferral: AffiliateReferralRef | null;
  lastTransactionId: string;
  usageVersion: number;
  graceExpiresAt: string | null;
  trialEndsAt: string | null;
  /** Timestamp when the trial started. Used to compute trial day for lifecycle emails. */
  trialStartedAt: string | null;
  /**
   * ISO timestamp of the trial→paid conversion, stamped centrally in
   * `updateOrgLedger` (A7Y-3778, D14) the moment a ledger's status flips to
   * "active" while its prior status was "trialing" or "trial_expired". A
   * ledger that was never trialing (born "active") keeps this `null`
   * forever. No history exists before the stamp shipped — see
   * `TRIAL_CONVERSION_TRACKED_SINCE` in `@a7y/platform-health`.
   */
  trialConvertedAt: string | null;
  /** Lifecycle emails sent during the trial period. Used for idempotency. */
  lifecycleEmails: LifecycleEmailRecord[];
  /** Stripe subscription_item ID (si_xxx) for the addon line item. */
  addonSubscriptionItemId: string | null;
  /** Current number of extra project addon slots (0 = none). */
  addonExtraProjectQuantity: number;
  /** Whether the Stripe subscription is scheduled to cancel at the end of the current period. */
  cancelAtPeriodEnd?: boolean;
  /** When set, entitlements resolve as this plan instead of `planId`. Used to grant free access. */
  overridePlanId?: PlanId | null;
  /** Downgrade held until the current period ends. `planId` stays the current plan until it arrives. */
  pendingPlanChange?: PendingPlanChange | null;
  createdAt: Date | string;
  updatedAt?: Date | string;
}

// ======================================================================================|
// MARK: Project Lifecycle

export type ProjectStatus = 'active' | 'archived' | 'suspended' | 'deleted';

export interface ProjectLifecycle {
  id: string;
  projectId: string;
  orgId: string;
  status: ProjectStatus;
  createdAt: Date | string;
  archivedAt?: Date | string;
  suspendedAt?: Date | string;
  deletedAt?: Date | string;
  lastActivityAt?: Date | string;
  usageHistory: BillingPeriod[];
  metadata?: Record<string, unknown>;
}

// ======================================================================================|
// MARK: Entitlement Denial (A7Y-481)

/**
 * Reasons a runtime entitlement check may deny access.
 *
 * `NO_SUBSCRIPTION` is reserved for future use (callers that want to fail-closed
 * when no ledger is present). `evaluateLedger` itself treats missing ledgers as
 * lenient — matching the existing `getOrgEntitlements()` default behavior.
 *
 * `QUOTA_EXCEEDED` is reserved for Phase B; not thrown in Phase A.
 */
export type EntitlementDenialCode =
  | 'TRIAL_EXPIRED'
  | 'SUBSCRIPTION_CANCELED'
  | 'SUBSCRIPTION_PAST_DUE'
  | 'NO_SUBSCRIPTION'
  | 'QUOTA_EXCEEDED';

export interface EntitlementDenial {
  code: EntitlementDenialCode;
  billingStatus: OrgBillingStatus | 'no_ledger';
  orgId: string;
  message: string;
}

/**
 * Pure function: inspect an OrgLedger and return an EntitlementDenial when the
 * org's billing status should block scheduled runtime work, or null when it's
 * cleared to run.
 *
 * Lenient: null ledger → null (allow).
 *
 * Override plans are authoritative. When `overridePlanId` is set we treat the
 * org as manually entitled regardless of transient Stripe billing state so
 * operator-issued plan overrides can unblock internal/staging orgs without
 * mutating ledger status.
 *
 * Blocks only the explicitly-blocked statuses (mirrors `BLOCKED_STATUSES` in
 * `entitlements_service.ts`): `trial_expired`, `canceled`, `past_due`.
 * `grace_overage`, `active`, `trialing`, `sandbox` all pass through.
 */
export function evaluateLedger(orgId: string, ledger: OrgLedger | null): EntitlementDenial | null {
  if (!ledger) return null;
  if (ledger.overridePlanId) return null;
  switch (ledger.status) {
    case 'trial_expired':
      return {
        code: 'TRIAL_EXPIRED',
        billingStatus: ledger.status,
        orgId,
        message: `Org ${orgId} trial has expired`,
      };
    case 'canceled':
      return {
        code: 'SUBSCRIPTION_CANCELED',
        billingStatus: ledger.status,
        orgId,
        message: `Org ${orgId} subscription is canceled`,
      };
    case 'past_due':
      return {
        code: 'SUBSCRIPTION_PAST_DUE',
        billingStatus: ledger.status,
        orgId,
        message: `Org ${orgId} subscription is past due`,
      };
    case 'active':
    case 'trialing':
    case 'grace_overage':
    case 'sandbox':
    default:
      return null;
  }
}

// ======================================================================================|
// MARK: Entitlements

export type FeatureKey =
  | 'project_creation'
  | 'market_creation'
  | 'store_publishing'
  | 'figma_plugin_access'
  | 'api_access'
  | 'priority_support'
  | 'store_analytics'
  | 'slack_integration'
  | 'team_collaboration'
  // A7Y-548 — module/feature flags introduced with Plan X tier structure.
  | 'localization'
  | 'keyword_analysis'
  | 'ai_discoverability'
  | 'competitor_discovery'
  | 'intent_mapping'
  | 'policy_checks'
  | 'review_sentiment'
  | 'asset_generation'
  | 'in_app_events';

// ======================================================================================|
// MARK: Feature Display Names (A7Y-551)

/**
 * Human-readable display name for each FeatureKey. Used by the webapp's
 * UpgradePrompt and any other UI that surfaces a feature-denied message.
 * Exhaustive over FeatureKey — the TypeScript compiler will error if a key
 * is added to FeatureKey without a display-name entry here.
 */
export const FEATURE_DISPLAY_NAMES: Record<FeatureKey, string> = {
  project_creation: 'Project creation',
  market_creation: 'Additional target languages',
  store_publishing: 'Direct store publishing',
  figma_plugin_access: 'Figma plugin',
  api_access: 'API access',
  priority_support: 'Priority support',
  store_analytics: 'Store analytics',
  slack_integration: 'Slack integration',
  team_collaboration: 'Team collaboration',
  localization: 'Localization & translation',
  keyword_analysis: 'Keyword analysis',
  ai_discoverability: 'AI discoverability',
  competitor_discovery: 'Competitor discovery',
  intent_mapping: 'Intent mapping',
  policy_checks: 'Store policy checks',
  review_sentiment: 'Reviews & sentiment',
  asset_generation: 'AI asset generation',
  in_app_events: 'In-app events',
};

export function getFeatureDisplayName(key: string): string {
  if (key in FEATURE_DISPLAY_NAMES) {
    return FEATURE_DISPLAY_NAMES[key as FeatureKey];
  }
  return key
    .split('_')
    .map((part) => (part.length === 0 ? part : part[0]!.toUpperCase() + part.slice(1)))
    .join(' ');
}

/**
 * Org-level usage quotas tracked against an org's ledger (project count,
 * market count, team-member count). These are the keys that populate the
 * `Record<OrgQuotaType, …>` usage arithmetic in entitlements responses.
 */
export type OrgQuotaType = 'projects' | 'markets' | 'team_members';

/**
 * Quota labels accepted by `quotaExceededResponse` / the webapp's
 * `useEntitlementGate`.
 *
 * A7Y-1707 — `custom_prompts` is a per-project cap (not an org-level usage
 * ledger like the others). It is surfaced as a `quotaType` so the
 * `quotaExceededResponse` envelope and the webapp's `useEntitlementGate` can
 * render a precise message, but it is intentionally NOT part of the
 * org-quota `Record<OrgQuotaType, …>` arithmetic.
 *
 * A7Y-3845 — `tracked_competitor_events`, `event_nominations` and
 * `asc_event_drafts` are the same shape: org-wide standing counts enforced
 * inline at the in-app-events routes, surfaced as a `quotaType` for the
 * `quotaExceededResponse` envelope and `useEntitlementGate`, but intentionally
 * NOT part of the org-quota `Record<OrgQuotaType, …>` arithmetic.
 */
export type QuotaType =
  | OrgQuotaType
  | 'custom_prompts'
  | 'tracked_keywords'
  | 'tracked_competitor_events'
  | 'event_nominations'
  | 'asc_event_drafts'
  | 'customer_demand_terms';

export interface EntitlementCheck {
  feature: FeatureKey;
  hasAccess: boolean;
  reason?: string;
}

export interface QuotaCheck {
  quotaType: QuotaType;
  limit: number;
  current: number;
  remaining: number;
  hasQuota: boolean;
  reason?: string;
}

export interface OrgEntitlements {
  orgId: string;
  planTier: PlanTier;
  features: Record<FeatureKey, boolean>;
  quotas: Record<OrgQuotaType, { limit: number; current: number }>;
  updatedAt: Date | string;
}

// ======================================================================================|
// MARK: API Request/Response Types

export interface CreateCheckoutSessionRequest {
  planId: string;
  billingInterval: BillingInterval;
  currency: SupportedCurrency;
  trialDays?: number;
  successUrl?: string;
  cancelUrl?: string;
  /**
   * A7Y-1896: client-supplied first-party affiliate referral id. UNTRUSTED —
   * the server re-validates it (exists + unexpired + campaign-coherent) before
   * stamping `a7y_referral` on the Stripe customer/session/subscription. Never
   * used as `client_reference_id`.
   */
  referralId?: string | null;
}

export interface CreateCheckoutSessionResponse {
  checkoutSessionUrl: string;
  sessionId: string;
}

export interface CreatePortalSessionRequest {
  returnUrl?: string;
}

export interface CreatePortalSessionResponse {
  portalSessionUrl: string;
}

export interface ChangePlanRequest {
  newPlanId: string;
  newBillingInterval: BillingInterval;
  prorate?: boolean;
}

export interface ChangePlanResponse {
  success: boolean;
  newPlanId: string;
  direction: 'upgrade' | 'downgrade' | 'same';
  /** The instant the plan applies: now for immediate, `pendingPlanChange.effectiveAt` for scheduled. */
  effectiveDate: string;
  effectiveTiming: 'immediate' | 'scheduled';
  pendingPlanChange: PendingPlanChange | null;
  prorationAmount?: number;
}

export interface InvoiceListItem {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  periodStart: number;
  periodEnd: number;
  invoicePdf?: string;
  invoiceUrl?: string;
}

export interface InvoiceListResponse {
  invoices: InvoiceListItem[];
  hasMore: boolean;
}

export interface EntitlementsResponse {
  orgId: string;
  planTier: PlanTier;
  billingInterval: BillingInterval;
  features: PlanFeatures;
  usage: UsageMetrics;
  quotas: Record<OrgQuotaType, { limit: number; current: number; remaining: number }>;
  blocked?: boolean;
  blockedReason?: string;
}

export interface PlanListResponse {
  plans: PlanDefinition[];
}

export interface QuotaCheckResult {
  allowed: boolean;
  currentCount: number;
  maxCount: number;
  availableSlots: number;
  status: OrgBillingStatus;
  upgradeUrl?: string;
  message?: string;
}

export interface UpdateAddonQuantityRequest {
  quantity: number;
}

export interface UpdateAddonQuantityResponse {
  success: boolean;
  addonId: string;
  previousQuantity: number;
  newQuantity: number;
  portalUrl?: string;
}

// ======================================================================================|
// MARK: Pricing Config (Firestore document — dynamic FX-derived pricing)

/** Per-plan pricing stored in Firestore, updated by the FX price updater. */
export interface PricingConfigPlan {
  currencies: Record<SupportedCurrency, CurrencyPricing>;
  stripePriceIds: PricingConfigPlanStripePriceIds | LegacyPricingConfigPlanPriceIds;
  /**
   * Stripe product IDs per environment. Source of truth is Firestore; the
   * `PLAN_TIERS.*.stripe.*.productId` constants are intentionally blank
   * (A7Y-545). The FX updater resolves productIds via `resolvePlanProductId`,
   * which prefers this field and falls back to PLAN_TIERS. Optional for
   * backward compatibility with pre-A7Y-607 documents.
   */
  stripeProductIds?: { sandbox: string; production: string };
}

/** Per-addon pricing stored in Firestore. */
export interface PricingConfigAddon {
  currencies: Record<SupportedCurrency, { monthly: number }>;
  stripePriceIds: { sandbox: string; production: string };
}

export interface LegacyPricingConfigPlanPriceIds {
  monthly: string;
  annual: string;
}

export interface PricingConfigPlanStripePriceIds {
  sandbox: { monthly: string; annual: string };
  production: { monthly: string; annual: string };
}

/**
 * Firestore document schema for dynamic pricing.
 * Stored at `_platform/pricing_config`.
 * Updated by the scheduled FX price updater function.
 */
export interface PricingConfig {
  fxRates: Record<SupportedCurrency, number>;
  plans: Record<string, PricingConfigPlan>;
  addons: Record<string, PricingConfigAddon>;
  lastChecked: string;
  lastUpdated: string;
}

export function isEnvironmentScopedPlanPriceIds(
  value: PricingConfigPlan['stripePriceIds']
): value is PricingConfigPlanStripePriceIds {
  return typeof value === 'object' && value !== null && 'sandbox' in value && 'production' in value;
}

// ======================================================================================|
// MARK: Currency Helpers

/** Get pricing for a specific currency from a plan definition. */
export function getPlanPricingForCurrency(
  plan: PlanDefinition,
  currency: SupportedCurrency
): CurrencyPricing {
  return plan.pricing.currencies[currency];
}

// ======================================================================================|
// MARK: FX Charm Rounding

/**
 * Round a minor-unit amount to the nearest charm price (major units ending in 9).
 * Examples:
 *   16132 (cents) → $161.32 → nearest X9 = $159 → 15900
 *   16832 (cents) → $168.32 → nearest X9 = $169 → 16900
 *   432   (cents) → $4.32   → nearest X9 = $9   →   900
 */
export function charmRound(amountInMinorUnits: number): number {
  const major = amountInMinorUnits / 100;
  const rounded = Math.round(major / 10) * 10 - 1;
  return Math.max(rounded, 9) * 100;
}

/**
 * Compute an annual price from a monthly charm price.
 * Applies the standard ~17% annual discount then charm-rounds.
 */
export function annualFromMonthly(monthlyMinorUnits: number): number {
  const rawAnnual = monthlyMinorUnits * 12 * 0.83;
  return charmRound(rawAnnual);
}

// ======================================================================================|
// MARK: Cancellation Survey

export type CancellationReason =
  | 'too_expensive'
  | 'missing_features'
  | 'not_using_enough'
  | 'switched_tool'
  | 'temporary_need'
  | 'too_difficult'
  | 'other';

export const CANCELLATION_REASONS: { id: CancellationReason; label: string }[] = [
  { id: 'too_expensive', label: 'Too expensive / not worth the price' },
  { id: 'missing_features', label: 'Missing features I need' },
  { id: 'not_using_enough', label: 'Not using it enough' },
  { id: 'switched_tool', label: 'Switched to a different tool' },
  { id: 'temporary_need', label: 'Only needed it temporarily' },
  { id: 'too_difficult', label: 'Too difficult to use' },
  { id: 'other', label: 'Other' },
];

export interface CancellationSurvey {
  id: string;
  orgId: string;
  userId: string;
  reasons: CancellationReason[];
  freetext: string | null;
  planAtCancellation: PlanId;
  billingInterval: BillingInterval;
  context: 'subscription_cancel' | 'org_delete';
  createdAt: string;
}

export interface SubmitExitSurveyRequest {
  reasons: CancellationReason[];
  freetext?: string | null;
  context: 'subscription_cancel' | 'org_delete';
}

export interface SubmitExitSurveyResponse {
  success: boolean;
  surveyId: string;
}

// ======================================================================================|
// MARK: Feature String Builder

/**
 * Format a `dataRetentionDays` value (-1/60/90/365/730) as a short, human
 * window label: `-1` → `'Unlimited'`, `>= 365` → `'{n} year(s)'`, else
 * `'{n} days'`. Single shared formatter for the plan feature strings, the
 * pricing comparison config, and the Competitor Activity boundary note —
 * do not re-derive this elsewhere.
 */
export function formatHistoryWindow(days: number): string {
  if (days === -1) return 'Unlimited';
  if (days >= 365) {
    const years = Math.round(days / 365);
    return `${years} year${years !== 1 ? 's' : ''}`;
  }
  return `${days} days`;
}

/**
 * Convert PlanFeatures into user-friendly display strings.
 * Used by both the subscription response builder (backend) and the plan selector (frontend).
 */
export function buildFeatureStrings(features: PlanFeatures): string[] {
  const strings: string[] = [];

  // Capacity
  strings.push(
    features.maxProjects === -1
      ? 'Unlimited projects'
      : `${features.maxProjects} project${features.maxProjects !== 1 ? 's' : ''}`
  );
  strings.push(
    features.maxTeamSeats === -1
      ? 'Unlimited team seats'
      : `${features.maxTeamSeats} team seat${features.maxTeamSeats !== 1 ? 's' : ''}`
  );
  strings.push(
    `${features.maxMarketsPerProject} target language${features.maxMarketsPerProject !== 1 ? 's' : ''} per project`
  );

  // Audit cadence
  const cadenceLabel: Record<PlanFeatures['maxAuditFrequency'], string> = {
    manual_only: 'Manual audits only',
    monthly: 'Monthly audits',
    biweekly: 'Biweekly audits',
    weekly: 'Weekly audits',
    '2x_week': '2× weekly audits',
    daily: 'Daily audits',
  };
  strings.push(cadenceLabel[features.maxAuditFrequency]);

  // Competitors — the limit is on competitors tracked by hand, not on the audit's
  // benchmark set, which has its own fixed size on every plan (A7Y-5544).
  if (features.maxTotalCompetitors != null) {
    strings.push(
      `${features.maxTotalCompetitors} competitor${features.maxTotalCompetitors !== 1 ? 's' : ''} tracked by hand in total`
    );
  } else {
    strings.push(`${features.maxCompetitorsPerProject} competitors tracked by hand per project`);
  }

  // Historical data ACCESS, not retention (A7Y-2490, TPF 2026-08-03).
  //
  // `dataRetentionDays` is applied only as a read cutoff — `getOrgRetentionCutoff`
  // clamps Firestore/BigQuery queries so a plan cannot read past its window. It
  // never deletes anything. The old "N-day data retention" wording promised
  // deletion the system does not perform, so the WORDING moves; implementing
  // deletion at the plan boundary was considered and rejected as a much larger,
  // irreversible change.
  if (features.dataRetentionDays === -1) {
    strings.push('Unlimited history access, incl. competitor change history');
  } else {
    strings.push(
      `${formatHistoryWindow(features.dataRetentionDays)} of history access, incl. competitor change history`
    );
  }

  // Core features
  if (features.includesStoreAnalytics) strings.push('Store analytics (ASC/GPC)');
  if (features.includesPublishing) strings.push('Direct publishing');
  if (features.assetGenerationLevel === 'text') strings.push('AI text generation');
  if (features.assetGenerationLevel === 'text_and_images')
    strings.push('AI text + image generation');
  if (features.includesLocalization) strings.push('Localization & translation');
  if (features.includesApiAccess) strings.push('API access');
  if (features.includesSlackIntegration) strings.push('Slack integration');
  if (features.includesFigmaPlugin) strings.push('Figma plugin');
  if (features.includesPrioritySupport) strings.push('Priority support');

  // In-app events (A7Y-3944): the three org-wide standing-count caps, in the
  // words the 403 upgrade modal uses for them.
  if (features.includesInAppEvents) {
    const caps = [
      features.maxTrackedCompetitorEvents,
      features.maxAscEventDrafts,
      features.maxEventNominations,
    ];
    if (caps.every((cap) => cap === -1)) {
      strings.push(
        'In-app events: unlimited tracked competitor events, event drafts and featuring nominations'
      );
    } else {
      const [tracked, drafts, nominations] = caps.map((cap) =>
        cap === -1 ? 'unlimited' : cap.toLocaleString('en-US')
      );
      strings.push(
        `In-app events: ${tracked} tracked competitor events, ${drafts} event drafts, ${nominations} featuring nominations`
      );
    }
  }

  // Audit modules
  if (features.includesReviewSentiment) {
    const lookback =
      features.reviewsLookbackDays === -1
        ? 'no date limit'
        : features.reviewsLookbackDays === 365
          ? '1 year'
          : `${features.reviewsLookbackDays}d`;
    strings.push(`Reviews & sentiment (${lookback})`);
  }
  if (features.includesKeywordAnalysis) strings.push('Keyword analysis');
  if (features.aiDiscoverabilityLLMs.length > 0) {
    strings.push(`AI discoverability (${features.aiDiscoverabilityLLMs.length}-LLM)`);
  }
  if (features.includesCompetitorDiscovery) strings.push('Competitor discovery');
  if (features.includesIntentMapping) strings.push('Intent mapping');
  if (features.includesPolicyChecks) strings.push('Store policy checks');

  return strings;
}

// ======================================================================================|
// MARK: A7Y-548 Feature Enforcement Helpers
//
// Pure helpers shared by the API, Firebase functions, and admin/webapp.
// Do NOT introduce runtime deps (no firebase-admin, no hono).

/**
 * Ordinal rank for audit cadence, from lowest → highest. `manual_only` is the
 * free-tier-only sentinel that blocks all scheduled cadence.
 */
export const AUDIT_FREQUENCY_RANK: Record<PlanFeatures['maxAuditFrequency'], number> = {
  manual_only: 0,
  monthly: 1,
  biweekly: 2,
  weekly: 3,
  '2x_week': 4,
  daily: 5,
};

/**
 * True when the requested user-facing cadence is permitted by the org's plan
 * features. Treats `manual_only` features as fully blocked (returns `false`
 * for any requested cadence). Open questions in the A7Y-548 plan resolved
 * `2x_week` to map to `weekly` at the cadence layer — no schema change.
 */
export function isAuditCadenceAllowed(
  requested: 'daily' | 'weekly' | 'biweekly' | 'monthly',
  features: Pick<PlanFeatures, 'maxAuditFrequency'>
): boolean {
  if (features.maxAuditFrequency === 'manual_only') return false;
  return AUDIT_FREQUENCY_RANK[requested] <= AUDIT_FREQUENCY_RANK[features.maxAuditFrequency];
}

/**
 * Resolve the highest concrete `AuditScheduleFrequency` a plan permits.
 *
 * `PlanFeatures['maxAuditFrequency']` has six values but `AuditScheduleFrequency`
 * only has four — there is no schedule value for `manual_only` or `2x_week`.
 * The A7Y-548 cadence layer already resolved that gap: `2x_week` collapses to
 * `weekly` (see `isAuditCadenceAllowed` / billing.ts:1146-1148), and
 * `manual_only` blocks all scheduled cadence. So:
 *   - `manual_only` → `null` (no schedule possible; caller should disable)
 *   - `2x_week`     → `'weekly'`
 *   - `daily` / `weekly` / `biweekly` / `monthly` → identity
 */
export function planMaxScheduleFrequency(
  features: Pick<PlanFeatures, 'maxAuditFrequency'>
): AuditScheduleFrequency | null {
  switch (features.maxAuditFrequency) {
    case 'manual_only':
      return null;
    case '2x_week':
      return 'weekly';
    case 'daily':
    case 'weekly':
    case 'biweekly':
    case 'monthly':
      return features.maxAuditFrequency;
  }
}

/**
 * The ordered list of `AuditScheduleFrequency` values a plan permits, from
 * lowest cadence to highest, capped at the plan's max. Returns `[]` for
 * `manual_only` plans (no scheduled cadence available). Reuses
 * `AUDIT_FREQUENCY_RANK` so the ordering stays in sync with the cadence layer.
 */
export function allowedScheduleFrequencies(
  features: Pick<PlanFeatures, 'maxAuditFrequency'>
): AuditScheduleFrequency[] {
  const planMax = planMaxScheduleFrequency(features);
  if (planMax === null) return [];
  const ordered: AuditScheduleFrequency[] = ['monthly', 'biweekly', 'weekly', 'daily'];
  return ordered.filter((f) => AUDIT_FREQUENCY_RANK[f] <= AUDIT_FREQUENCY_RANK[planMax]);
}

/**
 * Compute the earliest `created_at` value still within the org's retention
 * window. `-1` ⇒ unlimited retention ⇒ `null` (caller should NOT apply a
 * filter). `0` ⇒ now (nothing older than right now passes — useful for tests).
 */
export function getRetentionCutoff(
  features: Pick<PlanFeatures, 'dataRetentionDays'>,
  now: Date = new Date()
): Date | null {
  const days = features.dataRetentionDays;
  if (days < 0) return null;
  const cutoff = new Date(now);
  cutoff.setUTCDate(cutoff.getUTCDate() - days);
  return cutoff;
}

/**
 * Per-engine gating flags derived from a PlanFeatures. The three always-on
 * pillars (storetext / screenshot / icon) are literal `true` so callers can
 * use `modules.storetext` both as a boolean and as a narrowed type.
 *
 * `sentimentLookbackDays === -1` means "unlimited"; `null` means sentiment
 * should be skipped entirely (no review analysis for this plan).
 */
export interface AuditModuleFlags {
  storetext: true;
  screenshot: true;
  icon: true;
  localization: boolean;
  sentiment: boolean;
  sentimentLookbackDays: number | null;
  keyword: boolean;
  aiDiscoveryLLMs: string[];
  competitorDiscovery: boolean;
  intentMapping: boolean;
  policyChecks: boolean;
}

/**
 * Resolve which audit engine modules should run for the given plan features.
 * Used by the audit orchestrator to gate sentiment/keyword/AI-discovery/intent
 * /competitor engines, and by the content-engine to skip policy checks on
 * plans that don't include them.
 */
export function resolveAuditModules(features: PlanFeatures): AuditModuleFlags {
  return {
    storetext: true,
    screenshot: true,
    icon: true,
    localization: features.includesLocalization,
    sentiment: features.includesReviewSentiment,
    sentimentLookbackDays: features.reviewsLookbackDays,
    keyword: features.includesKeywordAnalysis,
    aiDiscoveryLLMs: features.aiDiscoverabilityLLMs,
    competitorDiscovery: features.includesCompetitorDiscovery,
    intentMapping: features.includesIntentMapping,
    policyChecks: features.includesPolicyChecks,
  };
}

/**
 * Report display caps applied to the audit report presentation layer.
 * These do NOT gate what the engine computes — they only bound how much of the
 * discovered set the free/anonymous report surfaces (A7Y-2078).
 */
export interface AuditReportDisplayLimits {
  /** null = uncapped (paid tiers show the full discovered set). */
  maxKeywords: number | null;
  maxCompetitors: number | null;
}

export const FREE_AUDIT_DISPLAY_LIMITS: AuditReportDisplayLimits = { maxKeywords: 10, maxCompetitors: 3 };

/**
 * Report display caps keyed on plan tier. Free (and anonymous → null → free)
 * see a partial view; every paid tier is uncapped. Display-only — the engine
 * computes the full set regardless (A7Y-2078).
 */
export function resolveAuditReportDisplayLimits(tier: PlanTier | null | undefined): AuditReportDisplayLimits {
  if (!tier || tier === 'free') return FREE_AUDIT_DISPLAY_LIMITS;
  return { maxKeywords: null, maxCompetitors: null };
}

/**
 * Human-readable skip reason emitted whenever the active plan does not include
 * a given audit module. Single source of truth for both the audit-engine writer
 * (`aso_audit_engine.ts::recordEngineSkip`) and the invariants reader
 * (`audit_output_invariants.ts`). Drift between writer and reader silently
 * breaks plan-gate detection — A7Y-1356.
 */
export const PLAN_SKIP_REASON_BY_ENGINE = {
  competitor: 'Plan does not include competitor discovery',
  sentiment: 'Plan does not include review sentiment',
  keyword: 'Plan does not include keyword analysis',
  intent: 'Plan does not include intent mapping',
  aiDiscovery: 'Plan does not include AI discoverability',
  l11nAnalysis: 'Plan does not include localization',
} as const satisfies Partial<Record<AsoEngineType, string>>;

export type PlanGatedEngine = keyof typeof PLAN_SKIP_REASON_BY_ENGINE;

export function getPlanSkipReason(engine: AsoEngineType): string | undefined {
  return (PLAN_SKIP_REASON_BY_ENGINE as Partial<Record<AsoEngineType, string>>)[engine];
}
